import React, { useEffect, useState } from "react";

import { Redirect, useLocation, useRouteMatch } from "react-router-dom";

import {
  IS_COGNITO_AUTH_ENABLED,
  STORYBOOK_DISABLE_AUTH_CHECK,
} from "@hubcapp/constants";
import { LoadingScreen } from "@hubcapp/components";
import { useAuth } from "@hubcapp/context";
import queryString from "query-string";

type PrivateRouteProps = {
  children: JSX.Element;
};

const PrivateRoute = ({ children }: PrivateRouteProps) => {
  const match = useRouteMatch();
  const location = useLocation();
  const { search } = useLocation<{ from: string }>();

  const urlParams: {
    app?: string;
    brand?: string;
    personKey?: string;
    agreementKey?: string;
  } = queryString.parse(search);

  const { isAuthenticated, randomSignInIsAuthenticated } = useAuth();

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const renderSignInRoute = () => {
    setIsLoggedIn(false);
    setIsLoading(false);
  };

  const renderRoute = () => {
    setIsLoggedIn(true);
    setIsLoading(false);
  };

  useEffect(() => {
    /**
     * Fixes - Warning: Can't perform a React state update on an unmounted component.
     * This is a no-op, but it indicates a memory leak in your application.
     * To fix, cancel all subscriptions and asynchronous tasks in a useEffect cleanup function.
     */
    let unmounted = false;

    if (STORYBOOK_DISABLE_AUTH_CHECK) {
      renderRoute();
    }

    const validateAuthentication = IS_COGNITO_AUTH_ENABLED
      ? isAuthenticated
      : randomSignInIsAuthenticated;

    validateAuthentication()
      .then(result => {
        if (!unmounted) {
          result ? renderRoute() : renderSignInRoute();
        }
      })
      .catch(() => {
        renderSignInRoute();
      });

    return () => {
      unmounted = true;
    };
  });

  /**
   * Load the page until we validate auth details
   */
  if (isLoading) {
    return <LoadingScreen />;
  }

  return isLoggedIn ||
    (urlParams?.brand && urlParams?.personKey) ||
    urlParams?.agreementKey ? (
    children
  ) : (
    <Redirect
      to={{
        pathname: urlParams?.app === "true" ? "/aa/sign-in" : "/brand-select",
        search: location.search,
        state: { from: match?.url },
      }}
    />
  );
};

export default PrivateRoute;
